import React, { useEffect } from 'react';
import {
  makeStyles,
  Theme,
  Grid,
  Paper,
} from '@material-ui/core';

import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';

import { SearchType } from '@backstage/plugin-search';
import {
  SearchBar,
  SearchFilter,
  SearchResult,
  useSearch,
} from '@backstage/plugin-search-react';
import {
  CatalogIcon,
  Content,
  DocsIcon,
  Header,
  Page,
} from '@backstage/core-components';
import { useApi, useAnalytics } from '@backstage/core-plugin-api';
import { Text } from '@backstage/ui';
import { captureSearchEvent } from '@internal/plugin-custom-search-ui';

const useStyles = makeStyles((theme: Theme) => ({
  bar: {
    padding: theme.spacing(1, 0),
  },
  filters: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  filter: {
    marginTop: theme.spacing(1.0),
    '& + &': {
      marginTop: theme.spacing(2),
    },
  },
  tip: {
    fontSize: '0.7rem',
  }
}));

const SearchPage = () => {
  const classes = useStyles();
  const { types, filters, setFilters, term, result } = useSearch();
  const catalogApi = useApi(catalogApiRef);
  const analytics = useAnalytics();
  const isTechdocsEnabled = types.includes('techdocs') || types.length === 0

  // Clean up the techdocs name filter when switching away from techdocs
  useEffect(() => {
    if (!isTechdocsEnabled && filters.name) {
      setFilters(prevFilters => {
        const { name, ...remainingFilters } = prevFilters;
        return remainingFilters;
      });
    }
  }, [types, filters, isTechdocsEnabled, setFilters]);

  // Capture search analytics when results load
  useEffect(() => {
    captureSearchEvent(analytics, result, term, types, filters);
  }, [result, term, types, filters, analytics]);

  return (
    <Page themeId="home">
      <Header title="Search" />
      <Content>
        <Grid container direction="row">
          <Grid item xs={12}>
            <SearchBar />
          </Grid>
          <Grid item xs={3}>
            <SearchType.Accordion
              name="Result Type"
              defaultValue=""
              types={[
                {
                  value: 'software-catalog',
                  name: 'Software Catalog',
                  icon: <CatalogIcon />,
                },
                {
                  value: 'techdocs',
                  name: 'Documentation',
                  icon: <DocsIcon />,
                },
              ]}
            />

            <Paper className={classes.filters}>
              {isTechdocsEnabled && (
                <>
                  <Text as="span" weight="bold" className={classes.tip}>💡 Use the filters for more relevant results</Text>
                  <SearchFilter.Autocomplete
                    className={classes.filter}
                    label="Documentation Name"
                    name="name"
                    values={async () => {
                      // Return a list of entities which are documented.
                      const { items } = await catalogApi.getEntities({
                        fields: ['metadata.name'],
                        filter: {
                          'metadata.annotations.backstage.io/techdocs-ref':
                          CATALOG_FILTER_EXISTS,
                        },
                      });

                      const names = items.map(entity => entity.metadata.name);
                      names.sort();
                      return names;
                    }}
                  />
                </>
              )}
              <SearchFilter.Autocomplete
                className={classes.filter}
                label="Owner"
                name="owner"
                values={async () => {
                  const { items } = await catalogApi.getEntities({
                    fields: ['spec.owner'],
                  });

                  const owners = items
                    .map(entity => entity.spec?.owner)
                    .filter((owner): owner is string => Boolean(owner));

                  const uniqueOwners = Array.from(new Set(owners));
                  uniqueOwners.sort();
                  return uniqueOwners;
                }}
              />
              <SearchFilter.Select
                className={classes.filter}
                label="Kind"
                name="kind"
                values={['Component', 'Template']}
              />
              <SearchFilter.Select
                className={classes.filter}
                label="Lifecycle"
                name="lifecycle"
                values={['experimental', 'production']}
              />
            </Paper>
          </Grid>
          <Grid item xs={9}>
            <SearchResult>
              <CatalogSearchResultListItem icon={<CatalogIcon />} />
              <TechDocsSearchResultListItem icon={<DocsIcon />} />
            </SearchResult>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};

export const searchPage = <SearchPage />;
